import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import '../hd-styles.css';

const API_URL = '/api';

function Settings() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [message, setMessage] = useState(null);

  const accountHash = localStorage.getItem('avalw_account_hash');

  useEffect(() => {
    if (!accountHash) {
      navigate('/login');
      return;
    }
    loadAccount();
  }, [accountHash, navigate]);

  const loadAccount = async () => {
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: accountHash })
      });
      const data = await res.json();
      if (data.success) {
        setAccount(data.data);
        setDisplayName(data.data.profile?.display_name || '');
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_hash: accountHash,
          display_name: displayName
        })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('display_name', displayName);
        setMessage({ type: 'success', text: 'Profile saved successfully!' });
        loadAccount();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to save' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('avalw_account_hash');
    localStorage.removeItem('display_name');
    navigate('/');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: 'Copied to clipboard!' });
    setTimeout(() => setMessage(null), 2000);
  };

  const formatNum = (num) => {
    if (num === null || num === undefined) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return Math.round(num).toLocaleString();
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="page-loading">
          <div className="spinner"></div>
          <p>Loading settings...</p>
        </div>
      </AppLayout>
    );
  }

  const badge = account?.badge || { name: 'Starter', color: '#64748b' };

  return (
    <AppLayout>
      <div className="page-content settings-page">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-left">
            <h1>Settings</h1>
            <p className="page-subtitle">Manage your account and preferences</p>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`message-banner ${message.type}`}>
            {message.type === 'success' ? (
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
            )}
            {message.text}
          </div>
        )}

        <div className="settings-grid">
          {/* Profile Card */}
          <div className="card settings-card">
            <div className="card-header">
              <h3>Profile</h3>
            </div>
            <div className="settings-content">
              <div className="profile-preview">
                <div className="profile-avatar" style={{ backgroundColor: badge.color }}>
                  {(displayName || 'U')[0]}
                </div>
                <div className="profile-badge" style={{ backgroundColor: badge.color + '20', color: badge.color }}>
                  {badge.name}
                </div>
              </div>

              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter display name"
                  maxLength={20}
                />
                <span className="form-hint">{displayName.length}/20 characters</span>
              </div>

              <button
                className="btn-primary"
                onClick={handleSaveProfile}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>

          {/* Account Info Card */}
          <div className="card settings-card">
            <div className="card-header">
              <h3>Account Information</h3>
            </div>
            <div className="settings-content">
              <div className="info-row">
                <span className="info-label">Account ID</span>
                <div className="info-value-row">
                  <span className="info-value mono">{accountHash?.substring(0, 12)}...</span>
                  <button className="copy-btn" onClick={() => copyToClipboard(accountHash)}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
                  </button>
                </div>
              </div>

              <div className="info-row">
                <span className="info-label">Account Type</span>
                <span className="info-value">{account?.account_type || 'home'}</span>
              </div>

              <div className="info-row">
                <span className="info-label">Status</span>
                <span className={`status-chip ${account?.worker_status || 'offline'}`}>
                  <span className="status-dot"></span>
                  {account?.worker_status === 'online' ? 'Online' : 'Offline'}
                </span>
              </div>

              <div className="info-row">
                <span className="info-label">Created</span>
                <span className="info-value">{account?.created_at ? new Date(account.created_at).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Stats Card */}
          <div className="card settings-card">
            <div className="card-header">
              <h3>Your Statistics</h3>
            </div>
            <div className="settings-content">
              <div className="stats-list">
                <div className="stat-row">
                  <span className="stat-icon blue">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>
                  </span>
                  <span className="stat-name">Uptime</span>
                  <span className="stat-val">{formatNum(account?.uptime_hours || 0)} hours</span>
                </div>
                <div className="stat-row">
                  <span className="stat-icon purple">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/></svg>
                  </span>
                  <span className="stat-name">Core Hours</span>
                  <span className="stat-val">{formatNum(account?.core_hours_total || 0)}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-icon green">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/></svg>
                  </span>
                  <span className="stat-name">CO2 Saved</span>
                  <span className="stat-val">{(account?.co2_saved_kg || 0).toFixed(2)} kg</span>
                </div>
                <div className="stat-row">
                  <span className="stat-icon orange">
                    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>
                  </span>
                  <span className="stat-name">Cache Served</span>
                  <span className="stat-val">{formatNum(account?.total_cache_served || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Worker Token Card */}
          <div className="card settings-card">
            <div className="card-header">
              <h3>Worker Setup</h3>
            </div>
            <div className="settings-content">
              <p className="settings-desc">Use this command to run your worker:</p>
              <div className="code-block">
                <code>npx @avalw/search-worker --token={accountHash?.substring(0, 20)}...</code>
                <button className="copy-btn" onClick={() => copyToClipboard(`npx @avalw/search-worker --token=${accountHash}`)}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
                </button>
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="card settings-card danger-card">
            <div className="card-header">
              <h3>Account Actions</h3>
            </div>
            <div className="settings-content">
              <button className="btn-danger" onClick={handleLogout}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

export default Settings;
